/**
 * Offline order-pricing engine.
 *
 * PURE — no Angular, no I/O, no `Date.now()`. Every input that could vary is passed in,
 * including `asOf`, so the same order priced twice gives the same answer and the whole thing
 * is provable from a fixture table. See docs/features/order-capture.md for the scenario matrix
 * this implements.
 *
 * Divergences from the Dynamics server plugin, deliberate and tested:
 *  - `net` is clamped at zero. Microsoft's formula does not clamp; a 120% discount produces
 *    `extendedamount = -5000` server-side (verified live). We refuse to show a negative price.
 *  - A discount rule bands on the line's combined quantity, or per delivery when the rule's
 *    `evaluationLevel` says split order; each delivery then carries its own percentage.
 */

import {
  DeliveryInput,
  FiringAllocation,
  LineOffer,
  OrderLineInput,
  PricedCell,
  PricedDelivery,
  PricedFiring,
  PricedLine,
  PricedOrder,
  PricingInput,
  PricingViolation,
  PricingViolationCode,
  PricingViolationSeverity,
  VIOLATION_MESSAGE_KEYS,
  currencyPrecisionOf,
  isMoneyAllocation,
  isSet,
  minorUnits,
  round2,
  roundMoney,
} from './pricing.model';
import {
  DiscountListType,
  DiscountValueType,
  FreeGoodEvaluationLevel,
  MinimumValueBasis,
  OfferRole,
  OfferShape,
  PricingDiscountList,
  PricingDiscountRule,
  PricingItem,
  PricingPriceList,
  ThresholdUsage,
} from '../../../interfaces/pricing/pricing-bundle.interface';
import { groupBy } from 'lodash-es';
import { resolveFreeGoods } from './free-goods';

/** No ceiling unless screen config supplies one — the bundle carries no max-discount field. */
const DEFAULT_MAX_DISCOUNT_PCT = 100;

/**
 * Date-only comparison on ISO strings.
 *
 * Both sides are truncated to `yyyy-MM-dd` and compared lexically, which is correct for ISO
 * and avoids `new Date()` timezone shifts moving a boundary by a day on a device in UTC+13.
 */
function onOrAfter(date: string, boundary: string | null): boolean {
  return !boundary || date.slice(0, 10) >= boundary.slice(0, 10);
}

function onOrBefore(date: string, boundary: string | null): boolean {
  return !boundary || date.slice(0, 10) <= boundary.slice(0, 10);
}

function isWithin(
  date: string,
  from: string | null,
  to: string | null,
): boolean {
  return onOrAfter(date, from) && onOrBefore(date, to);
}

/** An item the rep may order on `asOf`: within its validity dates. */
export function isItemOrderable(item: PricingItem, asOf: string): boolean {
  return isWithin(asOf, item.validFrom, item.validTo);
}

/** A list the engine applies on `asOf`: within its validity dates. */
export function isListActive(list: PricingDiscountList, asOf: string): boolean {
  return isWithin(asOf, list.validFrom, list.validTo);
}

/** Open-ended band: a null bound never excludes. */
function inBand(
  value: number,
  min: number | null,
  max: number | null,
): boolean {
  return !(isSet(min) && value < min) && !(isSet(max) && value > max);
}

/** Bundle fields are tri-state: null and undefined both mean "not configured". */

type ViolationContext = Partial<
  Pick<PricingViolation, 'product' | 'sequence' | 'limit' | 'actual'>
> & { name?: string; date?: string };

class ViolationCollector {
  private readonly items: PricingViolation[] = [];

  get all(): PricingViolation[] {
    return this.items;
  }

  get hasBlocker(): boolean {
    return this.items.some((v) => v.severity === 'block');
  }

  add(
    code: PricingViolationCode,
    severity: PricingViolationSeverity,
    context: ViolationContext = {},
  ): void {
    const { product, sequence, limit, actual, ...named } = context;
    // Everything that names a threshold or a value is a message parameter; ids are not.
    const params: Record<string, string | number> = { ...named };
    if (limit !== undefined) params.limit = limit;
    if (actual !== undefined) params.actual = actual;
    this.items.push({
      code,
      severity,
      product,
      sequence,
      limit,
      actual,
      messageKey: VIOLATION_MESSAGE_KEYS[code],
      params,
    });
  }
}

/** A priced line before discounts. Shared with `free-goods.ts`, which reads the quantities. */
export interface LineDraft {
  input: OrderLineInput;
  item: PricingItem | null;
  cells: PricedCell[];
  quantity: number;
  gross: number;
}

/**
 * Quantities keyed to a delivery the order actually declares.
 *
 * A quantity parked on a sequence that no longer exists — the rep removed a drop after typing
 * into it — is ignored rather than silently added to the total, which would make the line
 * total disagree with the columns on screen.
 */
function declaredQuantities(
  line: OrderLineInput,
  deliveries: DeliveryInput[],
): Array<{ sequence: number; quantity: number }> {
  return deliveries
    .map((d) => ({
      sequence: d.sequence,
      quantity: line.quantities?.[d.sequence] ?? 0,
    }))
    .filter((c) => c.quantity > 0);
}

export function priceOrder(input: PricingInput): PricedOrder {
  const { priceList, asOf } = input;
  const violations = new ViolationCollector();
  const maxDiscountPct = input.maxDiscountPct ?? DEFAULT_MAX_DISCOUNT_PCT;
  const deliveries = input.deliveries ?? [];
  const priorQuantities = input.priorQuantityByProduct ?? {};
  const precision = currencyPrecisionOf(priceList);

  validatePriceList(priceList, asOf, violations);

  const itemsByProduct = new Map<string, PricingItem>();
  for (const item of priceList.items ?? [])
    itemsByProduct.set(item.product, item);

  // ---- pass 1: quantities, gross, per-line validation ---------------------------------
  const drafts = buildLineDrafts(
    input.lines ?? [],
    deliveries,
    itemsByProduct,
    asOf,
    priorQuantities,
    violations,
    precision,
  );

  const orderGross = roundMoney(
    drafts.reduce((sum, d) => sum + d.gross, 0),
    precision,
  );
  const orderQuantity = drafts.reduce((sum, d) => sum + d.quantity, 0);

  // ---- pass 2: discounts --------------------------------------------------------------
  const activeLists = (priceList.discountLists ?? []).filter((l) =>
    isListActive(l, asOf),
  );
  const discountLists = activeLists.filter(
    (l) => l.type === DiscountListType.Discount,
  );

  const { lines, shares } = priceDiscounts(
    drafts,
    deliveries,
    discountLists,
    { maxDiscountPct, maxDiscountAmount: input.maxDiscountAmount },
    violations,
    precision,
  );

  // ---- pass 3: free goods -------------------------------------------------------------
  const freeGoods = resolveFreeGoods(
    activeLists.filter((l) => l.type === DiscountListType.FreeGoods),
    drafts,
    deliveries,
    itemsByProduct,
    input.includeUnmapped === true,
  );
  attachFreeGoodOffers(lines, freeGoods.offersByQualifier);
  lines.push(...freeGoods.lines);

  // ---- totals -------------------------------------------------------------------------
  // Free goods are extra units at no charge. They never move value, and they are excluded
  // from the quantity minimums — those gate what the account commits to buy.
  const chargeable = lines.filter((l) => !l.isFreeGood);
  const net = roundMoney(
    chargeable.reduce((sum, l) => sum + l.net, 0),
    precision,
  );
  // Chargeable gross, not `orderGross`, which spans every draft. Minimums gate what the
  // account commits to BUY, so free goods are out of both figures.
  const grossChargeable = roundMoney(
    chargeable.reduce((sum, l) => sum + l.gross, 0),
    precision,
  );

  const pricedDeliveries = sumDeliveries(deliveries, chargeable, precision);

  // Only when there is something to gate — an empty draft is incomplete, not invalid.
  if (chargeable.length > 0) {
    applyOrderMinimums(
      priceList,
      orderQuantity,
      grossChargeable,
      net,
      violations,
    );
    for (const delivery of pricedDeliveries)
      applyDeliveryMinimums(priceList, delivery, violations);
  }

  return {
    currency: priceList.currency,
    currencyPrecision: precision,
    priceListId: priceList.id,
    bundleVersion: input.bundleVersion ?? null,
    asOf,
    lines,
    deliveries: pricedDeliveries,
    gross: orderGross,
    net,
    totalQuantity: orderQuantity,
    lineCount: chargeable.length,
    deliveryCount: pricedDeliveries.filter((d) => d.quantity > 0).length,
    violations: violations.all,
    canSubmit: !violations.hasBlocker,
    firings: [
      ...discountFirings(shares, drafts, precision),
      ...freeGoods.firings,
    ],
  };
}

function validatePriceList(
  priceList: PricingPriceList,
  asOf: string,
  violations: ViolationCollector,
): void {
  if (!onOrAfter(asOf, priceList.validFrom)) {
    violations.add('priceListNotYetValid', 'block', {
      name: priceList.name,
      date: priceList.validFrom ?? '',
    });
  }
  if (!onOrBefore(asOf, priceList.validTo)) {
    violations.add('priceListExpired', 'block', {
      name: priceList.name,
      date: priceList.validTo ?? '',
    });
  }
}

function buildLineDrafts(
  lines: OrderLineInput[],
  deliveries: DeliveryInput[],
  itemsByProduct: Map<string, PricingItem>,
  asOf: string,
  priorQuantities: Record<string, number>,
  violations: ViolationCollector,
  precision: number,
): LineDraft[] {
  const drafts: LineDraft[] = [];
  for (const line of lines) {
    const draft = buildLineDraft(
      line,
      deliveries,
      itemsByProduct.get(line.product) ?? null,
      asOf,
      violations,
      precision,
    );
    if (!draft) continue;
    validateLineQuantity(draft, priorQuantities, violations);
    drafts.push(draft);
  }
  return drafts;
}

/** Every line priced under the discounts, with each delivery's share of every rule that applied. */
function priceDiscounts(
  drafts: LineDraft[],
  deliveries: DeliveryInput[],
  discountLists: PricingDiscountList[],
  {
    maxDiscountPct,
    maxDiscountAmount,
  }: { maxDiscountPct: number; maxDiscountAmount?: number },
  violations: ViolationCollector,
  precision: number,
): { lines: PricedLine[]; shares: DiscountShare[] } {
  const combinedAwards = awardDiscounts(
    discountLists,
    scopeOf(drafts, precision),
    'combined',
    violations,
  );
  // A split-order rule bands each delivery on that delivery's own quantities.
  const splitAwards = new Map(
    deliveries.map((d) => [
      d.sequence,
      awardDiscounts(
        discountLists,
        scopeOf(drafts, precision, d.sequence),
        'split',
        violations,
      ),
    ]),
  );
  const priced = drafts.map((draft) => {
    const product = draft.input.product;
    const combined = resolveDiscount(product, combinedAwards);
    const perCell = draft.cells.map((cell) =>
      resolveDiscount(product, splitAwards.get(cell.sequence)),
    );
    const line = priceLine(
      draft,
      combined,
      perCell,
      maxDiscountPct,
      violations,
      precision,
    );
    return { draft, line, combined, perCell };
  });
  const lines = priced.map(({ line }) => line);
  const sharesOf = () =>
    priced.flatMap(({ draft, line, combined, perCell }) =>
      discountShares(draft, line, combined, perCell, precision),
    );
  let shares = sharesOf();
  reconcileFlatAmounts(shares, lines, precision);
  // Last, or a flat amount's cents come back over the cap; the shares again, so its cut reads as one.
  if (capOrderDiscount(lines, maxDiscountAmount, violations, precision))
    shares = sharesOf();
  return { lines, shares };
}

/**
 * The price list's Max Discount Amount, on the whole order's discount after each line's ceiling.
 * Over it, every discounted cell gives back in proportion to what it took, so the total is the
 * cap to the cent.
 */
function capOrderDiscount(
  lines: PricedLine[],
  cap: number | undefined,
  violations: ViolationCollector,
  precision: number,
): boolean {
  if (cap === undefined || cap === null || cap < 0) return false;
  const cells = lines.flatMap((line) => line.perDelivery);
  const taken = cells.map((cell) =>
    roundMoney(cell.gross - cell.net, precision),
  );
  const total = roundMoney(
    taken.reduce((sum, amount) => sum + amount, 0),
    precision,
  );
  if (total <= cap) return false;
  const allowed = splitMinorUnits(cap, taken, precision);
  cells.forEach((cell, index) => {
    cell.net = roundMoney(cell.gross - allowed[index], precision);
    cell.discountPct =
      cell.gross > 0 ? round2((allowed[index] / cell.gross) * 100) : 0;
  });
  for (const line of lines) {
    line.net = roundMoney(
      line.perDelivery.reduce((sum, c) => sum + c.net, 0),
      precision,
    );
    if (line.gross > 0)
      line.effectiveDiscountPct = round2((1 - line.net / line.gross) * 100);
  }
  violations.add('orderDiscountCapped', 'warn', { limit: cap, actual: total });
  return true;
}

/** The chip belongs on the row the rep is typing in, not on the generated reward line. */
function attachFreeGoodOffers(
  lines: PricedLine[],
  offersByQualifier: Map<string, LineOffer[]>,
): void {
  for (const line of lines) {
    const offers = offersByQualifier.get(line.product);
    if (offers?.length) line.offers = [...line.offers, ...offers];
  }
}

/**
 * A line's quantities, gross and catalogue item. Null for a row the rep cleared — that is
 * not an order line and is never persisted.
 */
function buildLineDraft(
  line: OrderLineInput,
  deliveries: DeliveryInput[],
  item: PricingItem | null,
  asOf: string,
  violations: ViolationCollector,
  precision: number,
): LineDraft | null {
  const entered = declaredQuantities(line, deliveries);
  const quantity = entered.reduce((sum, c) => sum + c.quantity, 0);
  if (quantity <= 0) return null;

  if (!item) {
    violations.add('productNotOnPriceList', 'block', { product: line.product });
  } else if (!isItemOrderable(item, asOf)) {
    violations.add('itemNotValid', 'block', { product: line.product });
  }

  const unitPrice = item?.unitPrice ?? 0;
  const increment = item?.incrementQuantity ?? 0;
  const cells: PricedCell[] = entered.map(({ sequence, quantity: qty }) => {
    if (increment > 0 && qty % increment !== 0) {
      violations.add('quantityNotOnIncrement', 'block', {
        product: line.product,
        sequence,
        limit: increment,
        actual: qty,
      });
    }
    return {
      sequence,
      quantity: qty,
      gross: roundMoney(qty * unitPrice, precision),
      net: 0,
    };
  });

  return {
    input: line,
    item,
    cells,
    quantity,
    gross: roundMoney(
      cells.reduce((sum, c) => sum + c.gross, 0),
      precision,
    ),
  };
}

/**
 * min/max/quota are per order line, evaluated on the combined quantity — a split must not
 * let a rep dodge a minimum by spreading it across drops.
 */
function validateLineQuantity(
  draft: LineDraft,
  priorQuantities: Record<string, number>,
  violations: ViolationCollector,
): void {
  const { item, quantity } = draft;
  const product = draft.input.product;
  if (!item) return;

  if (isSet(item.minQuantity) && quantity < item.minQuantity) {
    violations.add('belowMinQuantity', 'block', {
      product,
      limit: item.minQuantity,
      actual: quantity,
    });
  }
  if (isSet(item.maxQuantity) && quantity > item.maxQuantity) {
    violations.add('aboveMaxQuantity', 'block', {
      product,
      limit: item.maxQuantity,
      actual: quantity,
    });
  }
  if (isSet(item.quotaPerAccount)) {
    const consumed = (priorQuantities[product] ?? 0) + quantity;
    if (consumed > item.quotaPerAccount) {
      violations.add('quotaExceeded', 'warn', {
        product,
        limit: item.quotaPerAccount,
        actual: consumed,
      });
    }
  }
}

/**
 * Applies the resolved and manual discounts under the tighter ceiling, cell by cell: a split-order
 * rule can give each delivery its own percentage on top of the combined one.
 */
function priceLine(
  draft: LineDraft,
  combined: ResolvedDiscount,
  perCell: ResolvedDiscount[],
  maxDiscountPct: number,
  violations: ViolationCollector,
  precision: number,
): PricedLine {
  const product = draft.input.product;
  const manualPct = resolveManualDiscount(draft);
  // Two ceilings can apply: the caller's and the one each winning rule carries. Both are limits,
  // so the tighter governs, manual included — a ceiling a manual discount could sidestep is none.
  // A split-order rule's ceiling holds only on the deliveries it applied to.
  const ceilingOf = (resolved: ResolvedDiscount | undefined) =>
    resolved?.ceilingPct ?? Number.POSITIVE_INFINITY;

  // Rounding once per cell keeps the per-delivery figures adding up to the order total exactly.
  const cells = draft.cells.map((cell, index) => {
    const ceilingPct = Math.min(
      maxDiscountPct,
      ceilingOf(combined),
      ceilingOf(perCell[index]),
    );
    const rawPct =
      combined.percentage + (perCell[index]?.percentage ?? 0) + manualPct;
    const cellPct = Math.min(rawPct, ceilingPct);
    if (rawPct > ceilingPct)
      violations.add('discountClamped', 'warn', {
        product,
        limit: ceilingPct,
        actual: round2(rawPct),
      });
    if (cellPct > 100)
      violations.add('negativeLineClamped', 'warn', {
        product,
        actual: round2(cellPct),
      });
    return {
      ...cell,
      net: roundMoney(cell.gross * Math.max(0, 1 - cellPct / 100), precision),
      discountPct: round2(cellPct),
    };
  });
  const net = roundMoney(
    cells.reduce((sum, c) => sum + c.net, 0),
    precision,
  );
  // Across cells the line's percentage is what it actually took off the gross.
  const effectivePct =
    draft.gross > 0
      ? round2((1 - net / draft.gross) * 100)
      : round2(
          Math.min(
            combined.percentage + manualPct,
            maxDiscountPct,
            ceilingOf(combined),
          ),
        );

  return {
    product,
    priceListItemId: draft.item?.id ?? null,
    productCategory: draft.item?.productCategory ?? null,
    unitPrice: draft.item?.unitPrice ?? 0,
    quantity: draft.quantity,
    perDelivery: cells,
    gross: draft.gross,
    volumeDiscountPct: round2(
      combined.percentage + weightedSplitPct(perCell, draft),
    ),
    manualDiscountPct: round2(manualPct),
    effectiveDiscountPct: effectivePct,
    net,
    freeUnits: 0,
    isFreeGood: false,
    appliedRuleIds: [
      ...new Set([...combined.ruleIds, ...perCell.flatMap((r) => r.ruleIds)]),
    ].sort(),
    offers: [...combined.offers, ...splitOffers(perCell, draft.cells)],
  };
}

/** One delivery's share of what one rule (or the manual entry, `match` null) took off a line. */
interface DiscountShare {
  product: string;
  sequence: number;
  match: RuleMatch | null;
  amount: number;
  /** What the rule asked for before a ceiling cut the delivery's total. */
  requested: number;
  /** The same, unrounded: a flat amount's pieces must add up to it exactly. */
  exact: number;
}

/**
 * Each delivery's discount split between the rules and the manual entry that made it, in
 * proportion to what each asked for, to the cent: the shares are what the firings allocate.
 */
function discountShares(
  draft: LineDraft,
  line: PricedLine,
  combined: ResolvedDiscount,
  perCell: ResolvedDiscount[],
  precision: number,
): DiscountShare[] {
  const manualPct = resolveManualDiscount(draft);
  return line.perDelivery.flatMap((cell, index) => {
    const parts: Array<{ match: RuleMatch | null; pct: number }> = [
      ...combined.applied,
      ...(perCell[index]?.applied ?? []),
    ].map((match) => ({ match, pct: match.percentage }));
    if (manualPct > 0) parts.push({ match: null, pct: manualPct });
    const requested = parts.map((part) => (cell.gross * part.pct) / 100);
    const amounts = splitMinorUnits(
      roundMoney(cell.gross - cell.net, precision),
      requested,
      precision,
    );
    return parts.map((part, i) => ({
      product: line.product,
      sequence: cell.sequence,
      match: part.match,
      amount: amounts[i],
      requested: roundMoney(requested[i], precision),
      exact: requested[i],
    }));
  });
}

/**
 * A flat amount is applied as a percentage and each delivery rounds on its own, so its pieces can
 * miss it by a cent or two. Those cents go where rounding cut the most (largest remainder), as the
 * web order grid apportions, so the order gets exactly the amount. A rule a ceiling cut is left
 * alone: that shortfall is real.
 */
function reconcileFlatAmounts(
  shares: DiscountShare[],
  lines: PricedLine[],
  precision: number,
): void {
  const units = (value: number) => minorUnits(value, precision);
  const lineOf = new Map(lines.map((line) => [line.product, line]));
  const touched = new Set<PricedLine>();
  for (const group of flatAmountGroups(shares)) {
    // A share given less than it asked for was cut by a ceiling; compared in cents, as floats misread 8.12 < 8.13 - 0.01.
    if (group.some((share) => units(share.amount) < units(share.requested) - 1))
      continue;
    const missing =
      units(group.reduce((sum, s) => sum + s.exact, 0)) -
      units(group.reduce((sum, s) => sum + s.amount, 0));
    if (missing !== 0 && Math.abs(missing) <= group.length)
      moveMinorUnits(group, missing, { lineOf, touched, precision });
  }
  for (const line of touched) {
    line.net = roundMoney(
      line.perDelivery.reduce((sum, c) => sum + c.net, 0),
      precision,
    );
    if (line.gross > 0)
      line.effectiveDiscountPct = round2((1 - line.net / line.gross) * 100);
  }
}

/** A pooled award is one amount over its products; any other flat rule is one amount per product. */
function flatAmountGroups(shares: DiscountShare[]): DiscountShare[][] {
  const flat = shares.filter(
    (share) => share.match?.list.valueType === DiscountValueType.FlatAmount,
  );
  return Object.values(
    groupBy(flat, ({ match, product }) =>
      [
        match?.list.id,
        match?.rule.id,
        match?.sequence ?? '',
        match?.pooled ? '' : product,
      ].join('|'),
    ),
  );
}

/** Moves `missing` cents (fils for KWD), one per share, onto the cells rounding moved furthest from what they asked. */
function moveMinorUnits(
  group: DiscountShare[],
  missing: number,
  {
    lineOf,
    touched,
    precision,
  }: {
    lineOf: Map<string, PricedLine>;
    touched: Set<PricedLine>;
    precision: number;
  },
): void {
  const step = Math.sign(missing);
  const unit = step / 10 ** precision;
  let left = Math.abs(missing);
  for (const share of [...group].sort(
    (a, b) => step * (b.exact - b.amount - (a.exact - a.amount)),
  )) {
    if (left === 0) return;
    const line = lineOf.get(share.product);
    const cell = line?.perDelivery.find((c) => c.sequence === share.sequence);
    if (!line || !cell) continue;
    const net = roundMoney(cell.net - unit, precision);
    if (net < 0 || net > cell.gross) continue;
    cell.net = net;
    share.amount = roundMoney(share.amount + unit, precision);
    left--;
    touched.add(line);
  }
}

/** `total` split by weight, largest remainder first, so the parts add up to it exactly. */
function splitMinorUnits(
  total: number,
  weights: number[],
  precision: number,
): number[] {
  const totalUnits = minorUnits(total, precision);
  const sum = weights.reduce((acc, weight) => acc + weight, 0);
  if (totalUnits <= 0 || sum <= 0) return weights.map(() => 0);
  const exact = weights.map((weight) => (totalUnits * weight) / sum);
  const parts = exact.map(Math.floor);
  let left = totalUnits - parts.reduce((acc, part) => acc + part, 0);
  for (const i of exact
    .map((_, index) => index)
    .sort((a, b) => exact[b] - parts[b] - (exact[a] - parts[a]) || a - b)) {
    if (left-- <= 0) break;
    parts[i]++;
  }
  return parts.map((part) => part / 10 ** precision);
}

/** One firing per (product, rule), or per rule for an order-wide one, plus one per product for a manual discount. */
function discountFirings(
  shares: DiscountShare[],
  drafts: LineDraft[],
  precision: number,
): PricedFiring[] {
  const groups = groupBy(
    shares.filter((s) => s.amount > 0),
    ({ match, product }) =>
      !match
        ? `${product}|manual`
        : `${match.orderWide ? '' : product}|${match.rule.id}`,
  );
  const cellsOf = new Map(
    drafts.map((draft) => [draft.input.product, draft.cells]),
  );
  return stampOrder(
    Object.values(groups).map((group) =>
      discountFiring(group, cellsOf, precision),
    ),
  );
}

/**
 * The server stamps each line's `indskr_applieddiscountrule` with the lowest-sequence rule firing
 * that gave it money, so every line's largest rule must come before the other rules on it. A
 * topological order over those constraints; a firing without a rule is never stamped.
 */
function stampOrder(firings: PricedFiring[]): PricedFiring[] {
  const ranked = [...firings].sort(byStampRank);
  const after = new Map<PricedFiring, Set<PricedFiring>>(
    ranked.map((f) => [f, new Set()]),
  );
  const waitingOn = new Map<PricedFiring, number>(ranked.map((f) => [f, 0]));
  for (const entries of ruleMoneyByLine(ranked))
    putLargestFirst(entries, after, waitingOn);
  return topologicalOrder(ranked, after, waitingOn);
}

/** Product first (order-wide last), then the larger amount, then rule id: the order when nothing constrains. */
function byStampRank(a: PricedFiring, b: PricedFiring): number {
  return (
    (a.productId ?? '\uffff').localeCompare(b.productId ?? '\uffff') ||
    b.amount - a.amount ||
    (a.ruleId ?? '').localeCompare(b.ruleId ?? '')
  );
}

/** On one line, the firing that gave the most must precede every other firing that gave money. */
function putLargestFirst(
  entries: Array<{ firing: PricedFiring; amount: number }>,
  after: Map<PricedFiring, Set<PricedFiring>>,
  waitingOn: Map<PricedFiring, number>,
): void {
  const largest = entries.reduce((best, e) =>
    e.amount > best.amount ? e : best,
  ).firing;
  for (const { firing } of entries) {
    if (firing === largest || after.get(largest)?.has(firing)) continue;
    after.get(largest)?.add(firing);
    waitingOn.set(firing, (waitingOn.get(firing) ?? 0) + 1);
  }
}

/** Per order line, every rule firing that put money on it. */
function ruleMoneyByLine(
  ranked: PricedFiring[],
): Array<Array<{ firing: PricedFiring; amount: number; line: string }>> {
  const money = ranked
    .filter((f) => f.ruleId)
    .flatMap((firing) =>
      firing.allocations.filter(isMoneyAllocation).map((a) => ({
        firing,
        amount: a.amount,
        line: `${a.product}|${a.sequence}`,
      })),
    );
  return Object.values(groupBy(money, (entry) => entry.line));
}

function topologicalOrder(
  ranked: PricedFiring[],
  after: Map<PricedFiring, Set<PricedFiring>>,
  waitingOn: Map<PricedFiring, number>,
): PricedFiring[] {
  const ordered: PricedFiring[] = [];
  const left = new Set(ranked);
  while (left.size) {
    // The first ready firing in rank order; on a cycle (two lines disagree), the first left.
    const next =
      ranked.find((f) => left.has(f) && !waitingOn.get(f)) ??
      ranked.find((f) => left.has(f));
    if (!next) break;
    left.delete(next);
    ordered.push(next);
    for (const later of after.get(next) ?? [])
      waitingOn.set(later, (waitingOn.get(later) ?? 1) - 1);
  }
  return ordered;
}

function discountFiring(
  group: DiscountShare[],
  cellsOf: Map<string, PricedCell[]>,
  precision: number,
): PricedFiring {
  const match = group[0].match;
  const { allocations, base } = moneyAllocations(group, cellsOf, precision);
  addQualifyingAllocations(group, cellsOf, allocations);
  const all = [...allocations.values()];
  const amount = roundMoney(
    all.reduce((sum, a) => sum + a.amount, 0),
    precision,
  );
  const requested = roundMoney(
    group.reduce((sum, share) => sum + share.requested, 0),
    precision,
  );
  return {
    ruleId: match?.rule.id ?? null,
    freeGoodId: null,
    listId: match?.list.id ?? null,
    productId: match?.orderWide ? null : group[0].product,
    baseAmount: roundMoney(base, precision),
    amount,
    // More than half a cent (fils) over: rounding alone never gets that far.
    uncappedAmount:
      requested > amount + 0.5 / 10 ** precision ? requested : null,
    evaluatedQuantity: all
      .filter((a) => a.role !== 'R')
      .reduce((sum, a) => sum + a.quantity, 0),
    rewardQuantity: null,
    repeatCount: null,
    qualifyingRuleIds: match?.qualifyingRuleIds ?? [],
    qualifyingFreeGoodIds: [],
    allocations: all,
  };
}

/** The lines the firing put money on, one allocation per line, and the gross they started from. */
function moneyAllocations(
  group: DiscountShare[],
  cellsOf: Map<string, PricedCell[]>,
  precision: number,
): { allocations: Map<string, FiringAllocation>; base: number } {
  const match = group[0].match;
  const allocations = new Map<string, FiringAllocation>();
  let base = 0;
  for (const share of group) {
    const cell = cellsOf
      .get(share.product)
      ?.find((c) => c.sequence === share.sequence);
    const key = `${share.product}|${share.sequence}`;
    const role = match?.qualifiers.includes(share.product) ? 'B' : 'R';
    if (!allocations.has(key)) base += cell?.gross ?? 0;
    const amount = roundMoney(
      (allocations.get(key)?.amount ?? 0) + share.amount,
      precision,
    );
    allocations.set(key, {
      product: share.product,
      sequence: share.sequence,
      freeGood: false,
      role,
      quantity: cell?.quantity ?? 0,
      amount,
    });
  }
  return { allocations, base };
}

/** Lines whose units earned the discount but took none of it, in the scope it was earned in. */
function addQualifyingAllocations(
  group: DiscountShare[],
  cellsOf: Map<string, PricedCell[]>,
  allocations: Map<string, FiringAllocation>,
): void {
  for (const source of new Set(
    group.map((share) => share.match).filter((m): m is RuleMatch => !!m),
  )) {
    for (const product of source.qualifiers) {
      const cells = (cellsOf.get(product) ?? []).filter(
        (cell) =>
          source.sequence === undefined || cell.sequence === source.sequence,
      );
      for (const cell of cells) {
        const key = `${product}|${cell.sequence}`;
        if (!allocations.has(key))
          allocations.set(key, {
            product,
            sequence: cell.sequence,
            freeGood: false,
            role: 'Q',
            quantity: cell.quantity,
            amount: 0,
          });
      }
    }
  }
}

/** The split-order percentage across the line, each delivery weighted by its gross. */
function weightedSplitPct(
  perCell: ResolvedDiscount[],
  draft: LineDraft,
): number {
  if (draft.gross <= 0) return 0;
  return (
    perCell.reduce(
      (sum, resolved, index) =>
        sum + resolved.percentage * (draft.cells[index]?.gross ?? 0),
      0,
    ) / draft.gross
  );
}

/** Each delivery's earned split offers, and the nearest next band among them, naming its delivery. */
function splitOffers(
  perCell: ResolvedDiscount[],
  cells: PricedCell[],
): LineOffer[] {
  const earned = new Map<string, LineOffer>();
  let nearest: LineOffer | null = null;
  perCell.forEach((resolved, index) => {
    for (const offer of resolved.offers) {
      if (offer.status === 'earned') earned.set(offer.ruleId, offer);
      else if (
        offer.away !== null &&
        (!nearest || (nearest.away ?? Infinity) > offer.away)
      )
        nearest = { ...offer, sequence: cells[index].sequence };
    }
  });
  return [...earned.values(), ...(nearest ? [nearest] : [])];
}

function sumDeliveries(
  deliveries: DeliveryInput[],
  chargeable: PricedLine[],
  precision: number,
): PricedDelivery[] {
  return deliveries.map((d) => {
    const cells = chargeable.flatMap((l) =>
      l.perDelivery.filter((c) => c.sequence === d.sequence),
    );
    return {
      sequence: d.sequence,
      quantity: cells.reduce((sum, c) => sum + c.quantity, 0),
      gross: roundMoney(
        cells.reduce((sum, c) => sum + c.gross, 0),
        precision,
      ),
      net: roundMoney(
        cells.reduce((sum, c) => sum + c.net, 0),
        precision,
      ),
    };
  });
}

function applyOrderMinimums(
  priceList: PricingPriceList,
  orderQuantity: number,
  grossChargeable: number,
  net: number,
  violations: ViolationCollector,
): void {
  const tested = minimumValue(
    priceList.minimumValueBasis,
    grossChargeable,
    net,
  );
  if (isSet(priceList.minOrderValue) && tested < priceList.minOrderValue) {
    violations.add('belowMinOrderValue', 'block', {
      limit: priceList.minOrderValue,
      actual: tested,
    });
  }
  if (
    isSet(priceList.minOrderQuantity) &&
    orderQuantity < priceList.minOrderQuantity
  ) {
    violations.add('belowMinOrderQuantity', 'block', {
      limit: priceList.minOrderQuantity,
      actual: orderQuantity,
    });
  }
}

function applyDeliveryMinimums(
  priceList: PricingPriceList,
  delivery: PricedDelivery,
  violations: ViolationCollector,
): void {
  // An empty drop is a column the rep has not filled in yet, not a violation.
  if (delivery.quantity <= 0) return;
  const { sequence } = delivery;
  const tested = minimumValue(
    priceList.minimumValueBasis,
    delivery.gross,
    delivery.net,
  );
  if (
    isSet(priceList.minDeliveryValue) &&
    tested < priceList.minDeliveryValue
  ) {
    violations.add('belowMinDeliveryValue', 'block', {
      sequence,
      limit: priceList.minDeliveryValue,
      actual: tested,
    });
  }
  if (
    isSet(priceList.minDeliveryQuantity) &&
    delivery.quantity < priceList.minDeliveryQuantity
  ) {
    violations.add('belowMinDeliveryQuantity', 'block', {
      sequence,
      limit: priceList.minDeliveryQuantity,
      actual: delivery.quantity,
    });
  }
}

// -------------------------------------------------------------------------------------
// discounts
// -------------------------------------------------------------------------------------

/**
 * The quantities one evaluation pass sees: the whole order, or one delivery for a split-order
 * rule. Every offer shape runs on these numbers, so split order needs no handlers of its own.
 */
interface Scope {
  quantity: Map<string, number>;
  gross: Map<string, number>;
  orderQuantity: number;
  /** The delivery a split-order pass sees; absent for the whole order. */
  sequence?: number;
}

/** What earned a match, for its firing: the products whose units qualified, in which scope. */
interface MatchSource {
  qualifiers: string[];
  qualifyingRuleIds: string[];
  /** An order-level rule naming no product: one firing for the whole order. */
  orderWide: boolean;
  sequence?: number;
}

interface RuleMatch extends MatchSource {
  list: PricingDiscountList;
  rule: PricingDiscountRule;
  percentage: number;
  /** One amount shared by every product it matched (order level, basket), not one per product. */
  pooled?: boolean;
  /** What the band was evaluated against, kept so an earned offer can report its threshold. */
  bandValue: number;
}

/** What every discount list awarded in one scope, per product, and what is still in reach. */
interface ScopeAwards {
  matches: Map<string, RuleMatch[]>;
  offers: Map<string, LineOffer[]>;
}

interface ResolvedDiscount {
  percentage: number;
  ruleIds: string[];
  applied: RuleMatch[];
  offers: LineOffer[];
  /**
   * Tightest discount ceiling carried by the rules that actually APPLIED, or null when none
   * of them configured one. Only `ThresholdUsage.Cap` counts — see `ruleCeiling`.
   */
  ceilingPct: number | null;
}

function scopeOf(
  drafts: LineDraft[],
  precision: number,
  sequence?: number,
): Scope {
  const scope: Scope = {
    quantity: new Map(),
    gross: new Map(),
    orderQuantity: 0,
    sequence,
  };
  for (const draft of drafts) {
    const cells =
      sequence === undefined
        ? draft.cells
        : draft.cells.filter((c) => c.sequence === sequence);
    const quantity = cells.reduce((sum, c) => sum + c.quantity, 0);
    if (quantity <= 0) continue;
    const product = draft.input.product;
    scope.quantity.set(product, (scope.quantity.get(product) ?? 0) + quantity);
    scope.gross.set(
      product,
      roundMoney(
        (scope.gross.get(product) ?? 0) +
          cells.reduce((sum, c) => sum + c.gross, 0),
        precision,
      ),
    );
    scope.orderQuantity += quantity;
  }
  return scope;
}

/**
 * A declared shape always wins. A list saved before the column existed is read from its rules,
 * as the web order grid does; Order Level cannot be told from Volume that way, so it needs the column.
 */
export function offerShapeOf(list: PricingDiscountList): OfferShape {
  if (
    isSet(list.offerShape) &&
    Object.values(OfferShape).includes(list.offerShape)
  )
    return list.offerShape;
  const rules = list.discountRules ?? [];
  if (!rules.some((r) => isSet(r.offerGroup)))
    return OfferShape.VolumeSingleProduct;
  const applied = rules.filter((r) => r.role !== OfferRole.Qualifying);
  if (applied.length > 0 && applied.every((r) => !r.product))
    return OfferShape.BasketSum;
  const qualifiers = new Set(
    rules.filter((r) => r.role === OfferRole.Qualifying).map((r) => r.product),
  );
  return applied.some((r) => qualifiers.has(r.product))
    ? OfferShape.CrossProductOnQualifier
    : OfferShape.CrossProductOnAnother;
}

export function isSplit(
  rule: Pick<PricingDiscountRule, 'evaluationLevel'> | undefined,
): boolean {
  return rule?.evaluationLevel === FreeGoodEvaluationLevel.SplitOrder;
}

/**
 * Whether a list has offers counting every delivery together, each delivery alone, or both, as the
 * engine evaluates them: a discount rule at its group's applied rule's level, free goods at their
 * reward row's.
 */
export function offerLevels(list: PricingDiscountList): {
  combined: boolean;
  split: boolean;
} {
  const rewards = (list.freeGoods ?? []).filter(
    (good) => good.role !== OfferRole.Qualifying,
  );
  return {
    combined:
      rulesAtLevel(list, 'combined').length > 0 ||
      rewards.some((good) => !isSplit(good)),
    split: rulesAtLevel(list, 'split').length > 0 || rewards.some(isSplit),
  };
}

/** A group is one offer, so its applied rule's level governs every member. */
function rulesAtLevel(
  list: PricingDiscountList,
  level: 'combined' | 'split',
): PricingDiscountRule[] {
  const wanted = level === 'split';
  return (list.discountRules ?? []).filter((rule) => {
    if (!isSet(rule.offerGroup)) return isSplit(rule) === wanted;
    return (
      isSplit(appliedRulesOf(list.discountRules, rule.offerGroup)[0]) === wanted
    );
  });
}

/** Applied rules of a group, by id, so every device picks the same one. */
function appliedRulesOf(
  rules: PricingDiscountRule[],
  group: number,
): PricingDiscountRule[] {
  return rules
    .filter((r) => r.offerGroup === group && r.role !== OfferRole.Qualifying)
    .sort((a, b) => a.id.localeCompare(b.id));
}

function groupsOf(rules: PricingDiscountRule[]): Array<{
  qualifiers: PricingDiscountRule[];
  applied: PricingDiscountRule | undefined;
}> {
  const groups = [...new Set(rules.map((r) => r.offerGroup).filter(isSet))];
  return groups.map((group) => ({
    qualifiers: rules.filter(
      (r) => r.offerGroup === group && r.role === OfferRole.Qualifying,
    ),
    applied: appliedRulesOf(rules, group)[0],
  }));
}

/** Several qualifiers in one group mean AND: each product must reach its own band. */
function qualifiersMet(
  qualifiers: PricingDiscountRule[],
  scope: Scope,
): boolean {
  return (
    qualifiers.length > 0 &&
    qualifiers.every((q) => {
      const quantity = q.product ? (scope.quantity.get(q.product) ?? 0) : 0;
      return quantity > 0 && inBand(quantity, q.minQuantity, q.maxQuantity);
    })
  );
}

function awardDiscounts(
  lists: PricingDiscountList[],
  scope: Scope,
  level: 'combined' | 'split',
  violations: ViolationCollector,
): ScopeAwards {
  const awards: ScopeAwards = { matches: new Map(), offers: new Map() };
  for (const list of lists) {
    const rules = rulesAtLevel(list, level);
    if (rules.length === 0) continue;
    switch (offerShapeOf(list)) {
      case OfferShape.OrderLevel:
        awardOrderLevel(list, rules, scope, awards);
        break;
      case OfferShape.BasketSum:
        awardBasket(list, rules, scope, awards);
        break;
      case OfferShape.CrossProductOnQualifier:
      case OfferShape.CrossProductOnAnother:
        awardCrossProduct(list, rules, scope, awards);
        break;
      default:
        awardVolume(list, rules, scope, awards, violations);
    }
  }
  return awards;
}

function sourceOf(
  scope: Scope,
  qualifiers: string[],
  qualifyingRuleIds: string[] = [],
): MatchSource {
  return {
    qualifiers,
    qualifyingRuleIds,
    orderWide: false,
    sequence: scope.sequence,
  };
}

function addMatch(
  awards: ScopeAwards,
  product: string,
  match: RuleMatch,
): void {
  addTo(awards.matches, product, match);
}

function addOffer(
  awards: ScopeAwards,
  product: string,
  offer: LineOffer | null,
): void {
  if (offer) addTo(awards.offers, product, offer);
}

/** Appends in place: copying the group on every add is quadratic in its size. */
function addTo<K, V>(groups: Map<K, V[]>, key: K, value: V): void {
  const group = groups.get(key);
  if (group) group.push(value);
  else groups.set(key, [value]);
}

/** Straight rules; a rule naming no product is offered to every product no named rule covers. */
function straightRulesByProduct(rules: PricingDiscountRule[]): {
  named: Map<string, PricingDiscountRule[]>;
  anyProduct: PricingDiscountRule[];
} {
  const named = new Map<string, PricingDiscountRule[]>();
  const anyProduct: PricingDiscountRule[] = [];
  for (const rule of rules) {
    if (isSet(rule.offerGroup) || rule.role === OfferRole.Qualifying) continue;
    if (rule.product) addTo(named, rule.product, rule);
    else anyProduct.push(rule);
  }
  return { named, anyProduct };
}

/** Overlapping bands: the tightest (highest) band wins; id breaks ties so devices agree. */
function tightestBand(
  rules: PricingDiscountRule[],
  value: number,
): PricingDiscountRule | undefined {
  return rules
    .filter((r) => inBand(value, r.minQuantity, r.maxQuantity))
    .sort(
      (a, b) =>
        (b.minQuantity ?? 0) - (a.minQuantity ?? 0) || a.id.localeCompare(b.id),
    )[0];
}

function awardVolume(
  list: PricingDiscountList,
  rules: PricingDiscountRule[],
  scope: Scope,
  awards: ScopeAwards,
  violations: ViolationCollector,
): void {
  const { named, anyProduct } = straightRulesByProduct(rules);
  for (const [product, quantity] of scope.quantity) {
    const candidates = named.get(product) ?? anyProduct;
    if (candidates.length === 0) continue;
    const gross = scope.gross.get(product) ?? 0;
    addOffer(
      awards,
      product,
      nextBandOffer(list, candidates, quantity, gross, 'quantity'),
    );
    if (
      candidates.filter((r) => inBand(quantity, r.minQuantity, r.maxQuantity))
        .length > 1
    ) {
      violations.add('overlappingTiers', 'warn', { product });
    }
    const band = tightestBand(candidates, quantity);
    if (band)
      addMatch(awards, product, {
        list,
        rule: band,
        percentage: rulePercentage(list, band, gross, quantity),
        bandValue: quantity,
        ...sourceOf(scope, [product]),
      });
  }
}

/** Bands on the order's total units. A rule naming no product discounts every line by one share. */
function awardOrderLevel(
  list: PricingDiscountList,
  rules: PricingDiscountRule[],
  scope: Scope,
  awards: ScopeAwards,
): void {
  const { named, anyProduct } = straightRulesByProduct(rules);
  const total = scope.orderQuantity;
  for (const [product, candidates] of named) {
    if (!scope.quantity.has(product)) continue;
    const gross = scope.gross.get(product) ?? 0;
    addOffer(
      awards,
      product,
      nextBandOffer(list, candidates, total, gross, 'order'),
    );
    const productBand = tightestBand(candidates, total);
    if (productBand)
      addMatch(awards, product, {
        list,
        rule: productBand,
        percentage: rulePercentage(list, productBand, gross, total),
        bandValue: total,
        ...sourceOf(scope, [...scope.quantity.keys()]),
      });
  }
  if (anyProduct.length === 0) return;
  const orderGross = [...scope.gross.values()].reduce((sum, g) => sum + g, 0);
  // One nudge for the whole order, on the row holding the most units, not a copy on every row.
  const lead = leadingProduct(scope.quantity.keys(), scope);
  if (lead)
    addOffer(
      awards,
      lead,
      nextBandOffer(list, anyProduct, total, orderGross, 'order'),
    );
  const band = tightestBand(anyProduct, total);
  if (!band) return;
  const percentage = rulePercentage(list, band, orderGross, total);
  const source = {
    ...sourceOf(scope, [...scope.quantity.keys()]),
    orderWide: true,
  };
  for (const product of scope.quantity.keys())
    addMatch(awards, product, {
      list,
      rule: band,
      percentage,
      bandValue: total,
      pooled: true,
      ...source,
    });
}

/** The basket is the qualifying rules' products; the applied rule bands on their combined units. */
function awardBasket(
  list: PricingDiscountList,
  rules: PricingDiscountRule[],
  scope: Scope,
  awards: ScopeAwards,
): void {
  for (const { qualifiers, applied } of groupsOf(rules)) {
    if (!applied) continue;
    if (!qualifiersMet(qualifiers, scope)) {
      addNudge(awards, qualifierNudge(list, qualifiers, applied, scope));
      continue;
    }
    const basket = [
      ...new Set(
        qualifiers
          .map((q) => q.product)
          .filter((p): p is string => !!p && scope.quantity.has(p)),
      ),
    ];
    const units = basket.reduce(
      (sum, p) => sum + (scope.quantity.get(p) ?? 0),
      0,
    );
    if (!inBand(units, applied.minQuantity, applied.maxQuantity)) {
      addNudge(awards, basketNudge(list, applied, basket, units, scope));
      continue;
    }
    const gross = basket.reduce((sum, p) => sum + (scope.gross.get(p) ?? 0), 0);
    const percentage = rulePercentage(list, applied, gross, units);
    const source = sourceOf(
      scope,
      basket,
      qualifiers.map((q) => q.id),
    );
    for (const product of basket)
      addMatch(awards, product, {
        list,
        rule: applied,
        percentage,
        bandValue: units,
        pooled: true,
        ...source,
      });
  }
}

/**
 * The qualifying bands govern; the applied rule names the product the money lands on. When that
 * product is not ordered, the qualifying rows say what adding it would earn.
 */
function awardCrossProduct(
  list: PricingDiscountList,
  rules: PricingDiscountRule[],
  scope: Scope,
  awards: ScopeAwards,
): void {
  for (const { qualifiers, applied } of groupsOf(rules)) {
    if (!applied?.product) continue;
    if (!qualifiersMet(qualifiers, scope)) {
      addNudge(awards, qualifierNudge(list, qualifiers, applied, scope));
      continue;
    }
    const target = applied.product;
    if (!scope.quantity.has(target)) {
      for (const q of qualifiers)
        addOffer(
          awards,
          q.product ?? '',
          groupNudge(list, applied, { requiredProduct: target }),
        );
      continue;
    }
    // With several qualifiers the smallest is what the offer turned on.
    const units = Math.min(
      ...qualifiers.map((q) => scope.quantity.get(q.product ?? '') ?? 0),
    );
    const gross = scope.gross.get(target) ?? 0;
    const source = sourceOf(
      scope,
      qualifiers.map((q) => q.product ?? ''),
      qualifiers.map((q) => q.id),
    );
    addMatch(awards, target, {
      list,
      rule: applied,
      percentage: rulePercentage(list, applied, gross, units),
      bandValue: units,
      ...source,
    });
  }
}

/** Below the basket's band: how many more across the basket, on its leading row. */
function basketNudge(
  list: PricingDiscountList,
  applied: PricingDiscountRule,
  basket: string[],
  units: number,
  scope: Scope,
): [string, LineOffer] | null {
  const lead = leadingProduct(basket, scope);
  if (!lead || !isSet(applied.minQuantity) || units >= applied.minQuantity)
    return null;
  const offer = groupNudge(list, applied, {
    measure: 'basket',
    threshold: applied.minQuantity,
    away: applied.minQuantity - units,
  });
  return offer && [lead, offer];
}

/** The row holding the most units; id breaks ties so devices agree. */
function leadingProduct(
  products: Iterable<string>,
  scope: Scope,
): string | undefined {
  return [...products].sort(
    (a, b) =>
      (scope.quantity.get(b) ?? 0) - (scope.quantity.get(a) ?? 0) ||
      a.localeCompare(b),
  )[0];
}

/**
 * What a started grouped offer still needs, once, on the row that can act on it: a qualifying
 * product not yet ordered, else the first one below its band. An offer no qualifying product has
 * been ordered for is not a nudge yet.
 */
function qualifierNudge(
  list: PricingDiscountList,
  qualifiers: PricingDiscountRule[],
  applied: PricingDiscountRule,
  scope: Scope,
): [string, LineOffer] | null {
  const lead = leadingProduct(
    qualifiers
      .map((q) => q.product)
      .filter((p): p is string => !!p && scope.quantity.has(p)),
    scope,
  );
  if (!lead) return null;
  const missing = qualifiers.find(
    (q) => q.product && !scope.quantity.has(q.product),
  );
  if (missing?.product) {
    const addProduct = groupNudge(list, applied, {
      requiredProduct: missing.product,
    });
    return addProduct && [lead, addProduct];
  }
  const short = qualifiers.find(
    (q) =>
      q.product && (scope.quantity.get(q.product) ?? 0) < (q.minQuantity ?? 0),
  );
  if (!short?.product || !isSet(short.minQuantity)) return null;
  const offer = groupNudge(list, applied, {
    threshold: short.minQuantity,
    away: short.minQuantity - (scope.quantity.get(short.product) ?? 0),
  });
  return offer && [short.product, offer];
}

/** A grouped offer's nudge. A flat amount has no percentage to promise, so it gets none. */
function groupNudge(
  list: PricingDiscountList,
  applied: PricingDiscountRule,
  overrides: Partial<LineOffer>,
): LineOffer | null {
  if (list.valueType === DiscountValueType.FlatAmount || !applied.percentage)
    return null;
  return {
    kind: 'discount',
    status: 'available',
    ruleId: applied.id,
    measure: 'quantity',
    threshold: null,
    away: null,
    percentage: applied.percentage,
    rewardProduct: applied.product,
    rewardUnits: null,
    ...overrides,
  };
}

function addNudge(
  awards: ScopeAwards,
  nudge: [string, LineOffer] | null,
): void {
  if (nudge) addOffer(awards, nudge[0], nudge[1]);
}

/** Lower priority wins; an unranked list sorts last. Then list and rule id, so devices agree. */
function byStrength(a: RuleMatch, b: RuleMatch): number {
  const rank = (m: RuleMatch) => m.list.priority ?? Number.POSITIVE_INFINITY;
  return (
    b.percentage - a.percentage ||
    rank(a) - rank(b) ||
    a.list.id.localeCompare(b.list.id) ||
    a.rule.id.localeCompare(b.rule.id)
  );
}

function resolveDiscount(
  product: string,
  awards: ScopeAwards | undefined,
): ResolvedDiscount {
  const matches = awards?.matches.get(product) ?? [];
  // Held back so the line reads "you have this, and this is next" rather than the reverse.
  const availableOffers = awards?.offers.get(product) ?? [];
  if (matches.length === 0)
    return {
      percentage: 0,
      ruleIds: [],
      applied: [],
      offers: availableOffers,
      ceilingPct: null,
    };

  // Non-stackable offers compete and only the best survives; stackable ones add on top of it.
  const ranked = [...matches].sort(byStrength);
  const winner = ranked.find((m) => !m.list.isStackable);
  const applied = [
    ...(winner ? [winner] : []),
    ...ranked.filter((m) => m.list.isStackable),
  ];

  const earnedOffers: LineOffer[] = applied.map((match) => ({
    kind: 'discount',
    status: 'earned',
    ruleId: match.rule.id,
    measure: 'quantity',
    threshold: match.rule.minQuantity ?? null,
    away: null,
    percentage: round2(match.percentage),
    rewardProduct: null,
    rewardUnits: null,
  }));

  // A ceiling is a limit, so when two applied rules disagree the lower one governs.
  const ceilings = applied
    .map((m) => ruleCeiling(m.rule))
    .filter((c): c is number => c !== null);

  return {
    percentage: applied.reduce((sum, m) => sum + m.percentage, 0),
    ruleIds: applied.map((m) => m.rule.id).sort(),
    applied,
    offers: [...earnedOffers, ...availableOffers],
    ceilingPct: ceilings.length ? Math.min(...ceilings) : null,
  };
}

/**
 * What one rule takes off `gross`, as a percentage of it, in the web order grid's order:
 * the count cap shrinks the base pro rata, *Maximum Value* caps the base, the value is taken,
 * *Maximum Discount* caps the value, and nothing exceeds the base. An unconfigured usage
 * changes nothing: the device must not pick one.
 */
function rulePercentage(
  list: PricingDiscountList,
  rule: PricingDiscountRule,
  gross: number,
  units: number,
): number {
  if (gross <= 0) return 0;
  const counted =
    isSet(rule.thresholdCount) &&
    rule.thresholdCount >= 0 &&
    units > rule.thresholdCount
      ? (gross * rule.thresholdCount) / units
      : gross;
  const { base, cap } = applyThresholdAmounts(rule, counted);
  const value =
    list.valueType === DiscountValueType.FlatAmount
      ? (rule.amount ?? 0)
      : (base * (rule.percentage ?? 0)) / 100;
  return (Math.max(0, Math.min(value, cap, base)) / gross) * 100;
}

/** *Maximum Value* shrinks the base; *Maximum Discount* caps what is taken. Amount first, then percentage. */
function applyThresholdAmounts(
  rule: PricingDiscountRule,
  counted: number,
): { base: number; cap: number } {
  let base = counted;
  let cap = Number.POSITIVE_INFINITY;
  if (isSet(rule.thresholdAmount)) {
    const amount = Math.max(0, rule.thresholdAmount);
    if (rule.thresholdUsage === ThresholdUsage.Base)
      base = Math.min(base, amount);
    if (rule.thresholdUsage === ThresholdUsage.Cap) cap = amount;
  }
  if (isSet(rule.thresholdPercentage)) {
    const share =
      (base * Math.min(100, Math.max(0, rule.thresholdPercentage))) / 100;
    if (rule.thresholdPercentageUsage === ThresholdUsage.Base) base = share;
    if (rule.thresholdPercentageUsage === ThresholdUsage.Cap)
      cap = Math.min(cap, share);
  }
  return { base, cap };
}

/**
 * The next band up, whether or not one is currently earned. This is the nudge the design
 * shows as "+10 more across deliveries" — it is rule knowledge, so the engine owns it.
 */
function nextBandOffer(
  list: PricingDiscountList,
  candidates: PricingDiscountRule[],
  bandValue: number,
  lineGross: number,
  measure: LineOffer['measure'],
): LineOffer | null {
  // A flat amount has no percentage to promise; its condition is stated instead.
  if (list.valueType === DiscountValueType.FlatAmount) return null;
  const nextBand = candidates
    .filter((r) => isSet(r.minQuantity) && r.minQuantity > bandValue)
    .sort((a, b) => (a.minQuantity ?? 0) - (b.minQuantity ?? 0))[0];
  if (!nextBand) return null;
  const threshold = nextBand.minQuantity ?? bandValue;
  // The line is worth more once it reaches the band; an amount cap is measured against that.
  const grossAtBand =
    measure === 'quantity' && bandValue > 0
      ? lineGross * (threshold / bandValue)
      : lineGross;
  return {
    kind: 'discount',
    status: 'available',
    ruleId: nextBand.id,
    measure,
    threshold,
    away: round2(threshold - bandValue),
    percentage: round2(rulePercentage(list, nextBand, grossAtBand, threshold)),
    rewardProduct: null,
    rewardUnits: null,
  };
}

/**
 * The value a price list's monetary minimums are tested against.
 *
 * ONE basis governs `minOrderValue` and `minDeliveryValue` together — that is deliberate in
 * the bundle contract, not an accident, so the two can never disagree about whether a
 * discount counts.
 *
 * A null basis keeps the pre-contract behaviour (net). That is a retained default, NOT an
 * inference that null means net: the contract is explicit that unconfigured is a real third
 * state, and changing what a null org does would silently move every existing minimum.
 */
function minimumValue(
  basis: MinimumValueBasis | null | undefined,
  gross: number,
  net: number,
): number {
  return basis === MinimumValueBasis.Gross ? gross : net;
}

/**
 * A rule's own discount ceiling, or null.
 *
 * `thresholdPercentage` is only a ceiling when `thresholdPercentageUsage` says so
 * (`Cap` — "the largest discount that may be given"); the line's manual discount is held to
 * it too. Under `Base` the same number shrinks the value instead (`rulePercentage`).
 *
 * A null usage is unconfigured, and is deliberately not treated as either option: the
 * bundle contract is explicit that null is a real third state.
 */
function ruleCeiling(rule: PricingDiscountRule): number | null {
  if (
    !isSet(rule.thresholdPercentage) ||
    rule.thresholdPercentageUsage !== ThresholdUsage.Cap
  )
    return null;
  return rule.thresholdPercentage;
}

/** Percentage wins when both are supplied — an amount is the fallback entry mode, not an add-on. */
function resolveManualDiscount(draft: LineDraft): number {
  const { manualDiscountPct, manualDiscountAmount } = draft.input;
  if (isSet(manualDiscountPct)) return manualDiscountPct;
  if (isSet(manualDiscountAmount) && draft.gross > 0)
    return (manualDiscountAmount / draft.gross) * 100;
  return 0;
}
